/**
 * useReviewQueueState — data + view state for the Needs-review queue
 * (BON-381), split out of the page so the page component stays a thin view
 * (the `useBookingsListState` pattern).
 *
 * The `resolved` filter is DERIVED, not stored: the desktop's Show-resolved
 * toggle asks for `include` (open rows plus their muted resolved siblings in
 * the same section), while the phone's Resolved tab asks for `only` (that tab
 * shows nothing else). Both default to `exclude`, so the page opens as a to-do
 * list.
 *
 * `loading` is FIRST-LOAD only. A refetch keeps the rows on screen and reports
 * `refreshing` instead, because the alternative was that every flick of the
 * Show-resolved toggle replaced the whole page with a spinner — the rows an
 * operator was reading vanished to be redrawn with one extra section, and on a
 * slow connection they lost their place entirely.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fetchReviewQueue } from './reviewApi';
import { REVIEW_CATEGORY_OPTIONS } from './reviewTypes';
import type { ReviewCategory, ReviewQueueResponse, ReviewResolvedFilter } from './reviewTypes';

export type MobileTab = 'open' | 'resolved';

/** An unrecognised `?category=` is treated as no filter rather than passed on —
 *  the API would 400 it, and a hand-edited or stale URL should degrade to the
 *  full list rather than to an error page. */
function readCategory(raw: string | null): ReviewCategory | null {
  return REVIEW_CATEGORY_OPTIONS.some((o) => o.value === raw) ? (raw as ReviewCategory) : null;
}

const EMPTY: ReviewQueueResponse = {
  openCount: 0,
  resolvedCount: 0,
  bookings: [],
  checkouts: [],
};

export function resolvedFilterFor(
  isPhone: boolean,
  showResolved: boolean,
  tab: MobileTab,
): ReviewResolvedFilter {
  if (isPhone) return tab === 'resolved' ? 'only' : 'exclude';
  return showResolved ? 'include' : 'exclude';
}

/**
 * The three view choices, held in the QUERY STRING rather than in component state.
 *
 * U8: opening an item and coming back unmounts this hook, so component state
 * silently reset — the operator was returned to a list that no longer contained
 * the row they had just been looking at. A URL also makes a view shareable and
 * survives a reload, which state cannot.
 *
 * Its own hook so the data hook below stays about DATA. Every write uses
 * `replace`, so flicking a toggle does not stack history entries that Back then
 * has to be pressed through one at a time, and "off" DROPS the parameter rather
 * than writing a falsy one.
 */
function useQueueViewParams() {
  const [params, setParams] = useSearchParams();

  const writeParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  return {
    showResolved: params.get('resolved') === '1',
    tab: (params.get('tab') === 'resolved' ? 'resolved' : 'open') as MobileTab,
    category: readCategory(params.get('category')),
    setShowResolved: useCallback(
      (on: boolean) =>
        writeParams((next) => (on ? next.set('resolved', '1') : next.delete('resolved'))),
      [writeParams],
    ),
    setTab: useCallback(
      (next: MobileTab) =>
        writeParams((p) => (next === 'resolved' ? p.set('tab', 'resolved') : p.delete('tab'))),
      [writeParams],
    ),
    setCategory: useCallback(
      (next: ReviewCategory | null) =>
        writeParams((p) => (next ? p.set('category', next) : p.delete('category'))),
      [writeParams],
    ),
  };
}

export function useReviewQueueState(isPhone: boolean) {
  const { showResolved, tab, category, setShowResolved, setTab, setCategory } =
    useQueueViewParams();

  const [data, setData] = useState<ReviewQueueResponse>(EMPTY);
  // Two separate flags, because they mean different things to the view: `loading`
  // is "there is nothing to show yet", `refreshing` is "what you are looking at
  // is about to be replaced". Only the first justifies a spinner.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const filter = resolvedFilterFor(isPhone, showResolved, tab);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    if (loadedOnce) setRefreshing(true);
    else setLoading(true);
    setError(null);
    fetchReviewQueue(filter, signal, category)
      .then((res) => {
        if (!signal.aborted) {
          setData(res);
          setLoadedOnce(true);
        }
      })
      .catch((err: unknown) => {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load the review queue');
      })
      .finally(() => {
        if (!signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `loadedOnce` only decides WHICH flag this run sets; as a dependency it would re-fire the fetch the moment the first load flipped it.
  }, [filter, category, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return {
    data,
    loading,
    refreshing,
    error,
    filter,
    showResolved,
    setShowResolved,
    tab,
    setTab,
    category,
    setCategory,
    reload,
  };
}
